import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiFetch, type AdminClientReport, type Case, type ProfessionalProfile } from "../api.js";
import { useAuth } from "../auth.js";

/**
 * The admin portal's front door.
 *
 * This exists because the ops surfaces were reachable only by typing their URLs,
 * which is not a portal — it is a set of pages that happen to be deployed. An
 * operator who has to remember /admin/registry does not know what else there is,
 * and CLAUDE.md §2A's whole point is that a non-technical operator can run this
 * platform without being told which paths exist.
 *
 * Every surface behind these links is INTERNAL OPS. That distinction is what
 * keeps the roster lawful: showing named professionals to an admin so they can
 * check enrolments and decide where to send work is the opposite of advertising
 * them to clients to attract work for them, which is what COMPLIANCE Constraint 1
 * forbids. None of this is reachable by a client, and the server refuses each
 * underlying action to every role but admin regardless of what this page renders.
 */
/**
 * The way back up. Every admin surface renders it, which is most of what turns
 * four separate pages into one portal an operator can move around in.
 */
export function AdminBreadcrumb() {
  return (
    <p className="breadcrumb">
      <Link href="/admin">← Admin</Link>
    </p>
  );
}

/**
 * WHAT NEEDS AN OPERATOR THIS MORNING.
 *
 * This page had no data fetching of any kind — seven links and prose — while its
 * own copy promised "the report counts beside them". So the answer to "is there
 * anything for me today" was: open four screens and read four lists.
 *
 * The numbers themselves are not new. AdminCases already computes exactly the
 * two that matter and explains them honestly; they were simply one click deep,
 * on the screen you only open if you already suspect there is work. This lifts
 * them to the door.
 *
 * WHAT IS DELIBERATELY NOT COUNTED: totals. "48 cases on the platform" is a
 * vanity number that competes for attention with the two that mean somebody has
 * to act. Only work-waiting-on-us is shown, and a queue at zero says so quietly
 * rather than dropping out — an operator needs to know the queue was CHECKED and
 * is empty, which is a different fact from no answer.
 */
function useAttention() {
  const { getToken } = useAuth();

  // Same query keys as the pages themselves, so opening a queue reuses this
  // fetch rather than repeating it, and acting on a queue invalidates both.
  const cases = useQuery({
    queryKey: ["admin", "cases"],
    queryFn: async () => apiFetch<{ cases: Case[] }>("/cases", { token: await getToken() }),
  });
  const pending = useQuery({
    queryKey: ["admin", "professionals", "pending"],
    queryFn: async () =>
      apiFetch<{ professionals: ProfessionalProfile[] }>("/admin/professionals?status=pending", {
        token: await getToken(),
      }),
  });
  const reports = useQuery({
    queryKey: ["admin", "reports", { status: "open", aboutUserId: undefined }],
    queryFn: async () =>
      apiFetch<{ reports: AdminClientReport[] }>("/admin/reports?status=open", {
        token: await getToken(),
      }),
  });

  return { cases, pending, reports };
}

/**
 * A count, or an honest absence of one.
 *
 * `undefined` while loading and on failure — NEVER 0. Rendering a zero for a
 * query that failed tells an operator there is no work waiting when there may be
 * a full queue, which is worse than telling them nothing: it is the one reading
 * that stops them looking.
 */
function Count({ n, label }: { n: number | undefined; label: string }) {
  if (n === undefined) return <small className="muted">count unavailable</small>;
  if (n === 0) return <small className="muted">nothing waiting</small>;
  return (
    <strong className="attention-count">
      {n} {label}
    </strong>
  );
}

export function AdminHome() {
  const { cases, pending, reports } = useAttention();

  // "Waiting on an operator" is routing or assignment — the two acts this
  // platform has built. A case in a work state is waiting on the attorney and is
  // not this screen's business, which is the same line AdminCases draws.
  const awaitingOps = cases.data
    ? cases.data.cases.filter(
        (c) => c.status === "SUBMITTED" || c.status === "VALIDATING" || c.status === "ROUTED",
      ).length
    : undefined;

  return (
    <section>
      <header className="page-head">
        <p className="eyebrow">Internal operations</p>
        <h2>Admin</h2>
        <p className="lede">
          Everything the platform is run from. These are internal operations
          screens — none of them is visible to a client, and nothing here is a
          public listing of professionals.
        </p>
      </header>

      <ul className="portal-links">
        <li>
          <Link href="/admin/cases">Case queue</Link>{" "}
          <Count n={awaitingOps} label="waiting on you" />
          <small>
            Every case on the platform, least-recently-touched first. This is
            where a submitted case is routed to a registry and where an attorney
            is put on it — until one of those happens, nothing moves.
          </small>
        </li>
        <li>
          <Link href="/admin/professionals">Verification queue</Link>{" "}
          <Count n={pending.data?.professionals.length} label="to check" />
          <small>
            Professionals waiting to be checked. Verifying one requires recording
            what you checked and how — that provenance is the thing that makes
            them assignable at all.
          </small>
        </li>
        <li>
          <Link href="/admin/roster">Professional roster</Link>
          <small>
            Everyone on the platform, their verification standing, and the report
            counts beside them. This is also where you stop routing work to
            someone.
          </small>
        </li>
        <li>
          <Link href="/admin/reports">Client reports</Link>{" "}
          <Count n={reports.data?.reports.length} label="unanswered" />
          <small>
            What clients have said about how their cases were handled, and the
            decisions the platform has taken about its own routing. Handling is
            by telephone — this queue is the record, not the conversation.
          </small>
        </li>
        {/*
          Directly after the roster and directly before the registry table,
          because it is the join between them: a seat is one verified professional
          plus one registry office, and an operator arriving here without either
          has nothing to seat.
        */}
        <li>
          <Link href="/admin/panel">Panel</Link>
          <small>
            Which verified attorneys are in the pool for which registry. Cases are
            assigned from it by rotation; overriding it is a separate, recorded act.
            A registry with no panel means its cases wait.
          </small>
        </li>
        <li>
          <Link href="/admin/registry">Registry offices</Link>
          <small>
            The jurisdiction table cases are routed on. An empty table routes
            nothing, on purpose.
          </small>
        </li>
        {/*
          Last, and deliberately so. It is the only screen here whose subject is
          people rather than cases, and it is the one that hands out the power
          every other screen on this list exercises — including the power to
          reach this screen. Its own copy carries the full consequence; this
          summary states what it is for and does not soften it.
        */}
        <li>
          <Link href="/admin/access">Roles and access</Link>
          <small>
            Changing what an existing account may do here, and the only way to
            make someone an administrator. You need their account id, which only
            they can give you — there is no directory of people to search.
          </small>
        </li>
      </ul>
    </section>
  );
}
